import * as React from "react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { DateRange } from "react-day-picker";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const MONTHS_PL = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

interface DropdownRangeDatePickerProps {
  from?: Date;
  to?: Date;
  label?: string;
  className?: string;
  onApply: (from: Date | undefined, to: Date | undefined) => void;
}

function DropdownRangeDatePicker({
  from,
  to,
  label,
  className,
  onApply,
}: DropdownRangeDatePickerProps) {
  const today = new Date();
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState<DateRange | undefined>(
    from || to ? { from, to } : undefined
  );
  const [month, setMonth] = React.useState((from ?? today).getMonth());
  const [year, setYear] = React.useState((from ?? today).getFullYear());

  const displayMonth = new Date(year, month, 1);

  const triggerLabel = React.useMemo(() => {
    if (from && to) {
      return `${format(from, "dd.MM.yyyy", { locale: pl })} – ${format(to, "dd.MM.yyyy", { locale: pl })}`;
    }
    if (from) return format(from, "dd.MM.yyyy", { locale: pl });
    return label ?? "Wybierz zakres dat";
  }, [from, to, label]);

  const handleOpen = (nextOpen: boolean) => {
    if (nextOpen) {
      setPending(from || to ? { from, to } : undefined);
      setMonth((from ?? today).getMonth());
      setYear((from ?? today).getFullYear());
    }
    setOpen(nextOpen);
  };

  const handleApply = () => {
    onApply(pending?.from, pending?.to);
    setOpen(false);
  };

  const handleClear = () => {
    setPending(undefined);
    onApply(undefined, undefined);
    setOpen(false);
  };

  const yearOptions = React.useMemo(
    () => Array.from({ length: 10 }, (_, i) => today.getFullYear() - 5 + i),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-8 justify-start gap-1.5 px-3 text-xs font-normal",
            !from && !to && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="h-3 w-3 shrink-0" />
          <span className="truncate">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="space-y-3">
          {/* Month / year dropdowns */}
          <div className="flex gap-2">
            <Select value={month.toString()} onValueChange={(val) => setMonth(Number(val))}>
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue placeholder="Miesiąc" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS_PL.map((name, i) => (
                  <SelectItem key={i} value={i.toString()} className="text-xs">
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={year.toString()} onValueChange={(val) => setYear(Number(val))}>
              <SelectTrigger className="h-8 w-[90px] text-xs">
                <SelectValue placeholder="Rok" />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={y.toString()} className="text-xs">
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Calendar */}
          <Calendar
            mode="range"
            selected={pending}
            onSelect={setPending}
            month={displayMonth}
            onMonthChange={(date) => {
              setMonth(date.getMonth());
              setYear(date.getFullYear());
            }}
            locale={pl}
            className="rounded-md border"
            initialFocus
          />

          {/* Actions */}
          <div className="flex justify-between pt-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={handleClear}
              disabled={!pending?.from && !pending?.to}
            >
              Wyczyść
            </Button>
            <Button size="sm" onClick={handleApply} disabled={!pending?.from}>
              Zastosuj
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { DropdownRangeDatePicker };
export type { DropdownRangeDatePickerProps };
